import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Send, Settings, Briefcase, Home, Sparkles, MessagesSquare, Plus, LogOut, Brain, Building2, ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { aviApi, aviStream } from "@/lib/aviApi";
import { BrandMark } from "@/components/BrandMark";
import { ModelSelector } from "@/components/ModelSelector";
import { cn } from "@/lib/utils";

const AVI_MODEL_KEY = "avi_chat_model";

const STARTERS = [
  "Remember that my mom's birthday is June 12",
  "What do you remember about me so far?",
  "Help me plan dinner with what I usually like",
  "Switch gears — let's talk about work",
];

export default function AviChat() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [profile, setProfile] = useState("personal");
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState(localStorage.getItem(AVI_MODEL_KEY) || "auto");
  const [resolvedModel, setResolvedModel] = useState("");
  const [resolvedProvider, setResolvedProvider] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [convos, setConvos] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const scrollRef = useRef(null);
  const contextTimer = useRef(null);

  // ---- auth guard ----
  useEffect(() => {
    (async () => {
      try {
        const [meR, modelR] = await Promise.all([aviApi.get("/auth/me"), aviApi.get("/me/models")]);
        setMe(meR.data);
        setProfile(meR.data.prefs.active_profile || "personal");
        const available = modelR.data.models || [];
        setModels(available);
        const stored = localStorage.getItem(AVI_MODEL_KEY);
        const selected = available.some((model) => model.id === stored) ? stored : modelR.data.default || "auto";
        setModelId(selected);
        localStorage.setItem(AVI_MODEL_KEY, selected);
      } catch (e) {
        navigate("/welcome");
      }
    })();
  }, [navigate]);

  const loadConvos = useCallback(async () => {
    try {
      const r = await aviApi.get("/me/conversations");
      setConvos(r.data.conversations || []);
    } catch (e) {}
  }, []);
  useEffect(() => { loadConvos(); }, [loadConvos]);

  // ---- automated context switching (geofence + schedule) ----
  useEffect(() => {
    if (!me) return;
    const hasPins = me.prefs.work_pin || me.prefs.home_pin;
    const hasSchedule = !!me.prefs.schedule;
    if (!hasPins && !hasSchedule) return;

    const evaluate = (coords) => {
      aviApi.post("/me/context", coords || {}).then((r) => {
        const d = r.data;
        if (d.switched) {
          setProfile(d.active_profile);
          const label = d.active_profile === "work" ? "Work" : "Personal";
          const why = d.reason.startsWith("geofence")
            ? (d.active_profile === "work" ? "you're at your workspace" : "you're home")
            : (d.active_profile === "work" ? "it's work hours" : "you're off the clock");
          toast(`Switched to ${label} — ${why}.`, { duration: 4000 });
        }
      }).catch(() => {});
    };

    const tick = () => {
      if (hasPins && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => evaluate({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
          () => evaluate({}),
          { maximumAge: 30000, timeout: 8000 }
        );
      } else {
        evaluate({});
      }
    };
    tick();
    contextTimer.current = setInterval(tick, 60000);
    return () => clearInterval(contextTimer.current);
  }, [me]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const switchProfile = async (p) => {
    if (p === profile) return;
    setProfile(p);
    try {
      await aviApi.put("/me/prefs", { active_profile: p });
      toast(`${p === "work" ? "Work" : "Personal"} profile active.`, { duration: 2500 });
    } catch (e) {}
  };

  const send = useCallback(async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setSending(true);
    setMessages((prev) => [...prev, { role: "user", content: msg }, { role: "assistant", content: "", chips: [], streaming: true }]);
    await aviStream(
      { message: msg, session_id: sessionId, model: modelId },
      {
        onMeta: (ev) => {
          setSessionId(ev.session_id);
          setResolvedModel(ev.resolved_model || "");
          setResolvedProvider(ev.resolved_provider || "");
        },
        onChips: (chips) => setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = { ...copy[copy.length - 1], chips };
          return copy;
        }),
        onToken: (tok) => setMessages((prev) => {
          const copy = [...prev];
          const last = { ...copy[copy.length - 1] };
          last.content += tok;
          copy[copy.length - 1] = last;
          return copy;
        }),
        onError: (err) => setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = { ...copy[copy.length - 1], content: err, streaming: false };
          return copy;
        }),
        onDone: () => {
          setMessages((prev) => {
            const copy = [...prev];
            copy[copy.length - 1] = { ...copy[copy.length - 1], streaming: false };
            return copy;
          });
          setSending(false);
          loadConvos();
        },
      }
    );
  }, [input, sending, sessionId, modelId, loadConvos]);

  const openConvo = async (sid) => {
    try {
      const r = await aviApi.get(`/me/conversations/${sid}`);
      setMessages((r.data.messages || []).map((m) => ({
        role: m.role, content: m.content, chips: m.chips || [], streaming: false,
      })));
      setSessionId(sid);
      const lastModel = [...(r.data.messages || [])].reverse().find((m) => m.model_selection);
      if (lastModel && models.some((model) => model.id === lastModel.model_selection)) {
        setModelId(lastModel.model_selection);
        localStorage.setItem(AVI_MODEL_KEY, lastModel.model_selection);
        setResolvedModel(lastModel.resolved_model || "");
        setResolvedProvider(lastModel.resolved_provider || "");
      }
      setDrawerOpen(false);
    } catch (e) {
      toast.error("Couldn't open that chat.");
    }
  };

  const newChat = () => {
    setMessages([]);
    setSessionId(null);
    setResolvedModel("");
    setResolvedProvider("");
    setDrawerOpen(false);
  };

  const logout = async () => {
    try { await aviApi.post("/auth/logout"); } catch (e) {}
    navigate("/welcome");
  };

  const firstName = (me?.prefs?.display_name || me?.name || "").split(" ")[0];

  return (
    <div className="consumer min-h-screen avi-warmth flex flex-col" data-testid="avi-chat-page">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur-md">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between gap-1 sm:gap-3">
          <div className="hidden sm:flex items-center gap-2.5 min-w-0">
            <BrandMark className="h-9 w-9" />
            <div className="leading-tight min-w-0">
              <div className="font-display font-semibold text-[15px]">Avi</div>
              <div className="text-[11px] text-muted-foreground truncate" data-testid="avi-access-status">
                {me?.is_super_admin
                  ? <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Founder · Contextual Intelligence</span>
                  : me?.enterprise_linked && profile === "work"
                  ? <span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3" /> {me.enterprise_name}</span>
                  : "remembers you"}
              </div>
            </div>
          </div>

          {/* Profile toggle pill */}
          <div className="flex items-center rounded-full border border-border bg-secondary/70 p-1 shrink-0" data-testid="profile-toggle">
            {[
              { key: "personal", label: "Personal", Icon: Home },
              { key: "work", label: "Work", Icon: Briefcase },
            ].map(({ key, label, Icon }) => (
              <button
                key={key}
                onClick={() => switchProfile(key)}
                data-testid={`profile-toggle-${key}`}
                className={cn(
                  "flex items-center gap-1 rounded-full px-2 sm:px-3 py-1.5 text-xs font-medium transition-colors",
                  profile === key ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                )}
              >
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-0 sm:gap-1 shrink-0">
            <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Conversations" data-testid="conversations-button">
                  <MessagesSquare className="h-4.5 w-4.5 h-4 w-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="consumer w-[86vw] sm:w-[360px] p-0 bg-card">
                <SheetHeader className="px-4 py-3 border-b border-border">
                  <SheetTitle className="font-display text-sm flex items-center justify-between">
                    Your conversations
                    <Button size="sm" variant="secondary" onClick={newChat} data-testid="avi-new-chat-button">
                      <Plus className="h-3.5 w-3.5" /> New
                    </Button>
                  </SheetTitle>
                </SheetHeader>
                <ScrollArea className="h-[calc(100vh-60px)] p-3">
                  <div className="space-y-1.5" data-testid="avi-conversations-list">
                    {convos.length === 0 && (
                      <p className="text-xs text-muted-foreground p-2">Chats you have with Avi will appear here.</p>
                    )}
                    {convos.map((c, i) => (
                      <button
                        key={c.session_id}
                        onClick={() => openConvo(c.session_id)}
                        data-testid={`avi-convo-${i}`}
                        className={cn(
                          "w-full text-left rounded-xl border px-3 py-2.5 transition-colors",
                          sessionId === c.session_id ? "border-primary/40 bg-primary/5" : "border-border hover:bg-secondary/60"
                        )}
                      >
                        <div className="text-xs font-medium truncate">{c.title}</div>
                        <div className="mt-0.5 flex items-center justify-between text-[10px] text-muted-foreground">
                          <span className="capitalize">{c.profile}</span>
                          <span>{c.message_count} msgs</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              </SheetContent>
            </Sheet>
            <Link to="/app/settings">
              <Button variant="ghost" size="icon" aria-label="Settings" data-testid="avi-settings-button">
                <Settings className="h-4 w-4" />
              </Button>
            </Link>
            <Button variant="ghost" size="icon" aria-label="Log out" onClick={logout} data-testid="avi-logout-button">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="border-b border-border bg-card/60">
        <div className="max-w-3xl mx-auto px-4 py-2">
          <ModelSelector
            value={modelId}
            models={models}
            onChange={(value) => { setModelId(value); localStorage.setItem(AVI_MODEL_KEY, value); }}
            disabled={sending || models.length === 0}
            testIdPrefix="avi-model"
          />
        </div>
      </div>

      {/* Thread */}
      <main className="flex-1 overflow-hidden">
        <div ref={scrollRef} className="h-full overflow-y-auto scrollbar-thin">
          <div className="max-w-3xl mx-auto px-4 py-6 space-y-4" data-testid="avi-thread">
            {messages.length === 0 ? (
              <div className="pt-16 text-center" data-testid="avi-empty-state">
                <BrandMark className="h-14 w-14 mx-auto" testId="avi-empty-state-brand-mark" />
                <h2 className="font-display text-2xl font-semibold mt-4">
                  {firstName ? `Hey ${firstName} 👋` : "Hey there 👋"}
                </h2>
                <p className="text-sm text-muted-foreground mt-1.5" data-testid="avi-empty-state-subtitle">
                  {me?.is_super_admin && profile === "work"
                    ? "Contextual Intelligence is active across every sanitized workspace module."
                    : profile === "work" ? "Work mode — pipelines, plans and workspace knowledge." : "I'm all ears. Tell me anything — I'll remember."}
                </p>
                <div className="flex flex-wrap justify-center gap-2 mt-6">
                  {STARTERS.map((s, i) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      data-testid={`avi-starter-${i}`}
                      className="rounded-full border border-border bg-card px-3.5 py-2 text-xs text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors shadow-sm"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((m, i) => (
                <div key={i} className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}>
                  <div className={cn("max-w-[85%] sm:max-w-[75%]")}>
                    <div
                      className={cn(
                        "px-4 py-3 text-sm leading-6 whitespace-pre-wrap",
                        m.role === "user" ? "avi-bubble-user" : "avi-bubble-ai",
                        m.streaming && !m.content && "stream-caret"
                      )}
                      data-testid={m.role === "user" ? `avi-user-bubble-${i}` : `avi-ai-bubble-${i}`}
                    >
                      {m.content}
                      {m.streaming && m.content && <span className="stream-caret" />}
                    </div>
                    {m.role === "assistant" && (m.chips?.length || 0) > 0 && !m.streaming && (
                      <div className="flex flex-wrap gap-1.5 mt-1.5 pl-1" data-testid={`avi-memory-chips-${i}`}>
                        {m.chips.map((c, j) => (
                          <span
                            key={j}
                            data-testid={`avi-memory-chip-${i}-${j}`}
                            className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary/60 px-2 py-0.5 text-[10px] text-muted-foreground"
                          >
                            {c.type === "enterprise" ? <Building2 className="h-2.5 w-2.5" /> : <Brain className="h-2.5 w-2.5" />}
                            {c.label}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </main>

      {/* Composer */}
      <footer className="border-t border-border bg-card/80 backdrop-blur-md">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="flex items-end gap-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
              }}
              rows={1}
              placeholder={profile === "work" ? "Ask about your work world…" : "Tell Avi anything…"}
              data-testid="avi-input"
              className="min-h-[46px] max-h-36 resize-none rounded-2xl bg-secondary/50"
            />
            <Button
              onClick={() => send()}
              disabled={sending || !input.trim()}
              size="icon"
              className="h-[46px] w-[46px] rounded-2xl shrink-0"
              data-testid="avi-send-button"
              aria-label="Send"
            >
              {sending ? <Sparkles className="h-4 w-4 animate-pulse" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground text-center mt-2">
            Avi remembers what matters
            {resolvedModel && <span data-testid="avi-resolved-model"> · {resolvedProvider} / {resolvedModel}</span>}
          </p>
        </div>
      </footer>
    </div>
  );
}
